import { useMemo, useState } from 'react';
import { Container, Divider, ListItemIcon, Stack, styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { MenuItem } from './components/item';
import { MenuColumn, MenuRow } from './components/column';
import { AppTypography, StackColumn, StackRow } from 'src/components';
import { ContactUs } from './components/contact-us';
import { FeatureArticles } from './components/feature-article';
import { MenuLink } from './components/link';
import { SidebarItemProps } from '../sidebar/sidebar';
import TextMaxLine from 'src/components/TextMaxLine';
import { IconLock } from 'src/libraries/icons';
import { getFeaturesRequirements } from 'src/api/featuresApi';
import { NoFeatureAccessBanner } from 'src/components/banners/no-feature-access-banner';

//
//

const PARENT_ITEM_HEIGHT = 74;

//

const StyledPaper = styled(Container)(({ theme }) => ({
  width: '100%',
  left: 0,
  right: 0,
  position: 'absolute',
  top: PARENT_ITEM_HEIGHT,
  zIndex: theme.zIndex.modal,
  filter: 'drop-shadow(1px 1px 6px rgba(0, 0, 0, 0.25))',
  minHeight: 250,
  display: 'flex',
  backgroundColor: theme.palette.background.paper,
}));

type MegaMenuProps = {
  megaMenuNavConfig: SidebarItemProps[];
};

export const MegaMenu = ({ megaMenuNavConfig, ...other }: MegaMenuProps) => {
  return (
    <Stack direction="row" spacing={0} color="text.primary" {...other}>
      {megaMenuNavConfig.map((parent) => (
        <MegaMenuSection key={parent.title} parent={parent} />
      ))}
    </Stack>
  );
};

//

const MegaMenuSection = ({ parent }: { parent: any }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  const getFeaturesRequirementsHook = useQuery({
    queryKey: ['features-requirements'],
    queryFn: () => getFeaturesRequirements(),
  });

  const disabledMenuItems = parent?.children?.filter((child: any) => child?.disabled);

  const lowestDisabledFeature = useMemo(() => {
    return (
      getFeaturesRequirementsHook?.data?.data
        .filter((f: { featureCode: string }) =>
          disabledMenuItems?.some(
            (item: { disabled?: boolean; featureCode?: string }) =>
              item.disabled && item.featureCode === f.featureCode
          )
        )
        .sort((a: any, b: any) => a.requiredPlan - b.requiredPlan)[0] || null
    );
  }, [disabledMenuItems, getFeaturesRequirementsHook?.data?.data]);

  if (!parent) return null;

  if (parent?.children?.length) {
    return (
      <>
        <MenuItem
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
          {...parent}
          open={open}
          hasSub
        />

        {open && (
          <StyledPaper maxWidth="xl" onMouseEnter={handleOpen} onMouseLeave={handleClose}>
            <MenuRow>
              {parent.name === 'programmes' ? (
                <>
                  <MenuColumn>
                    {parent.children.slice(0, 3).map((item: SidebarItemProps) => (
                      <MegaMenuSectionItem
                        key={item.id || item.title}
                        icon={item.icon}
                        to={item.path}
                        description={item.subtitle}
                        defaultPath={item?.defaultPath}
                        {...item}
                      />
                    ))}
                  </MenuColumn>

                  {/* Rest of items */}

                  {!!parent.children.slice(3)?.length && (
                    <>
                      <Divider orientation="vertical" />
                      <MenuColumn>
                        <AppTypography variant="system" color="grey.300">
                          My programmes
                        </AppTypography>

                        {parent.children.slice(3).map((item: SidebarItemProps) => (
                          <MegaMenuSectionItem
                            key={item.id || item.title}
                            icon={item.icon}
                            to={item.path}
                            defaultPath={item?.defaultPath}
                            description={item.subtitle}
                            {...item}
                          />
                        ))}
                      </MenuColumn>
                    </>
                  )}
                </>
              ) : (
                <MenuColumn>
                  {parent.children?.map((item: SidebarItemProps) => (
                    <MegaMenuSectionItem
                      key={item.id || item.title}
                      icon={item.icon}
                      to={item.path}
                      defaultPath={item?.defaultPath}
                      description={item.subtitle}
                      {...item}
                      // disabled={parent.name === 'projects' ? false : item?.disabled}
                    />
                  ))}
                </MenuColumn>
              )}

              {lowestDisabledFeature ? (
                <MenuColumn showBackground sx={{ p: 0 }}>
                  <NoFeatureAccessBanner
                    isNavigation
                    menuSection={parent.name}
                    planTier={lowestDisabledFeature?.requiredPlan}
                    title={noAccessMenuBannerCopies?.find(copy => copy?.code === parent?.name)?.title || ''}
                    description={noAccessMenuBannerCopies?.find(copy => copy?.code === parent?.name)?.subtitle || ''}
                  />
                </MenuColumn>
              ) : (
                <>
                  <ContactUs name={parent?.name} />

              {parent?.name === 'communications' && (
                <MenuColumn showBackground>
                  <AppTypography fontWeight="bold" variant="body2">
                    Featured articles
                  </AppTypography>

                  <FeatureArticles />
                </MenuColumn>
              )}
            </>
              )}
            </MenuRow>
          </StyledPaper>
        )}
      </>
    );
  }

  return <MenuItem {...parent} />;
};

//

type MegaMenuSectionItemProps = {
  icon: any;
  defaultPath?: string;
  to: string;
  title: string;
  description?: string;
  disabled?: boolean;
};

export const MegaMenuSectionItem: React.FC<MegaMenuSectionItemProps> = ({
  icon,
  defaultPath,
  to = '#',
  title,
  description,
  disabled,
}) => {
  return (
    <StackRow alignItems="flex-start">
      {icon && (
        <ListItemIcon sx={{ mr: 0, width: 18, height: 18, '& svg': { width: '100%', height: '100%' } }}>
          {icon}
        </ListItemIcon>
      )}

      <StackColumn gap={0}>
        <MenuLink to={defaultPath || to} title={title} endIcon={disabled ? <IconLock sx={{ height: 16 }} /> : null} />

        {description && (
          <TextMaxLine line={3} variant="body2">
            {description}
          </TextMaxLine>
        )}
      </StackColumn>
    </StackRow>
  );
};

//

const noAccessMenuBannerCopies = [
  { code: 'projects', title: 'Subscribe to purchase carbon offsets', subtitle: 'You can explore all available projects, but a subscription is required to purchase carbon offsets. ' },
  { code: 'my-offsets', title: 'Subscribe to unlock footprints and offsetting', subtitle: "You can view your past offsets here, but you'll need a subscription to calculate new footprints and purchase offsets through the portal." },
  { code: 'programmes', title: 'Subscribe to provide carbon offsetting options to your customers', subtitle: "CarbonClick offers a range of tools to help your customers reduce or offset their emissions – from calculators and reduction tips to fully integrated offsetting experiences. We can help you design a programme that fits your business." },
  { code: 'communications', title: 'Subscribe to tell your sustainability story', subtitle: "Communications tools help you tell your climate story – from showcasing your impact to sharing the projects you support. Use ready-made assets, widgets, and shareable pages to engage your audience and show the impact you're making." },
];
